import React, { useEffect, useRef } from "react";
import useLatest from "../../hooks/useLatest";

export default function HotSeatScene({
  outcome,
  participants,
  durationMs,
  greenCarpet,
  onComplete,
  onStatus,
  onReady,
  onTransition,
  onRevealFrame,
  onFallback,
}) {
  const host = useRef(null);
  const controller = useRef(null);
  const latest = useLatest({
    outcome,
    participants,
    onComplete,
    onStatus,
    onReady,
    onTransition,
    onRevealFrame,
    onFallback,
  });

  useEffect(() => {
    const abort = new AbortController();
    let failed = false;
    const fail = () => {
      if (abort.signal.aborted || failed) return;
      failed = true;
      abort.abort();
      latest.current.onFallback();
    };
    const timeout = setTimeout(fail, 25000);
    import("./createCasinoScene")
      .then(({ createCasinoScene }) => {
        if (abort.signal.aborted) return null;
        return createCasinoScene(host.current, {
          signal: abort.signal,
          durationMs,
          greenCarpet,
          onError: fail,
          onReady: () => {
            if (abort.signal.aborted) return;
            clearTimeout(timeout);
            latest.current.onReady?.();
          },
          onStatus: (value) => latest.current.onStatus(value),
          onTransition: (value) => {
            if (abort.signal.aborted) return;
            latest.current.onTransition?.(value);
          },
          onRevealFrame: (value) => latest.current.onRevealFrame?.(value),
          onComplete: (value) => {
            if (abort.signal.aborted) return;
            latest.current.onComplete(value);
          },
        });
      })
      .then((scene) => {
        if (!scene || abort.signal.aborted) {
          scene?.dispose();
          return;
        }
        controller.current = scene;
        scene.setOutcome(latest.current.outcome, latest.current.participants);
      })
      .catch(fail);
    return () => {
      clearTimeout(timeout);
      abort.abort();
      controller.current?.dispose();
      controller.current = null;
    };
  }, [durationMs, greenCarpet, latest]);

  useEffect(() => {
    controller.current?.setOutcome(outcome, participants);
  }, [outcome, participants]);

  return (
    <div
      ref={host}
      style={{ width: "100%", height: "100%" }}
      role="img"
      aria-label="Sala de casino con cuatro islas de máquinas tragamonedas"
    />
  );
}
